import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { SearchSelect, type SSGroup, type SSOption } from "../ui/SearchSelect";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { useIsMobile } from "../../lib/useIsMobile";
import { parseTrNumber } from "../../lib/trNumber";
import { MCard } from "../mobile/primitives";
import "./QuickCountSection.css";

/**
 * HIZLI SAYIM — kullanıcı kendi çalışma listesini kurar (kategori ve/veya tek kart), tabloda sistem miktarını
 * görür, SAYILAN miktarı girer, fark tek tıkla düzeltilir (count_diff hareketi, sebep "sayim_farki").
 * Liste kullanıcıya ÖZEL ve sunucuda kalıcıdır (StockCountEntry) → başka cihazdan girince de yerinde durur.
 */
const KIND_LABEL: Record<string, string> = { stock: "Hammadde / Sarf", semi: "Yarı Mamul", finished: "Mamul" };
/** Hızlı filtre: yalnız son X günde hareket görmüş kalemler (hareketsizler sayım kuyruğundan elenir). */
const MOVED_SEGMENTS: Array<[number | null, string]> = [
  [null, "Tümü (filtre yok)"], [1, "Son 1 gün"], [3, "Son 3 gün"], [7, "Son 7 gün"],
  [14, "Son 14 gün"], [30, "Son 30 gün"], [60, "Son 60 gün"], [90, "Son 90 gün"],
];
/** "bugün" / "3 gün önce" / "90+ gün" — 90 günlük pencerenin dışı null gelir. */
function lastMovedLabel(iso: string | Date | null): string {
  if (!iso) return "90+ gün";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "bugün";
  if (days === 1) return "dün";
  return `${days} gün önce`;
}
const nf = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 });
const fmt = (n: number) => nf.format(n);
/**
 * Sayılan miktar girişi → sayı; boş/geçersiz/negatifte null.
 * KANONİK `parseTrNumber` kullanılır: "1.234,5"→1234,5 ve "12.5"→12,5 (noktayı koşulsuz silmek
 * "12.5"i 125 yapıp hayalî stok yazdırırdı — bellekteki parseTrNumber gotcha'sı).
 */
function parseQty(v: string): number | null {
  if (!v.trim()) return null;
  const n = parseTrNumber(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function QuickCountSection() {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [movedDays, setMovedDays] = useState<number | null>(null); // son X günde hareket görenler
  const listQ = trpc.inventory.countList.useQuery(movedDays ? { movedWithinDays: movedDays } : {});
  const typesQ = trpc.settings.itemType.list.useQuery({ includeInactive: false }, { staleTime: 60_000 });
  const itemsQ = trpc.inventory.itemOptions.useQuery(undefined, { staleTime: 60_000 });

  const addMut = trpc.inventory.countAddEntry.useMutation({ onSuccess: () => { void qc.invalidateQueries(); } });
  const removeMut = trpc.inventory.countRemoveEntry.useMutation({ onSuccess: () => { void qc.invalidateQueries(); } });
  const applyMut = trpc.inventory.applyCount.useMutation();
  /**
   * SAYIMDA LOT AÇMA — kalemin hiç lotu yoksa fazlalık yazılacak yer olmaz ve sayım
   * "Mal Kabul'e gidin" diye bölünürdü. Sistem miktarı 0 iken depo sorulur; seçilirse
   * sunucu `origin: opening_stock` ile lot açar (lot zaten varsa bu alan yok sayılır).
   */
  const locsQ = trpc.location.list.useQuery(undefined, { staleTime: 60_000 });
  const stokDepolari = useMemo(
    // Sunucudaki STOCK_LOC_TYPES ile AYNI küme — ayrışırsa kullanıcı seçebildiği ama
    // sunucunun reddettiği bir depo görür ("stok tutamaz" hatası).
    () => (locsQ.data ?? []).filter((l) => ["depo", "raf", "bolge", "mal_kabul", "karantina"].includes(l.type)),
    [locsQ.data],
  );
  const [yeniLotDepo, setYeniLotDepo] = useState("");
  /**
   * AÇIK SAYIM TUTANAĞI — fark uygulandıkça sunucuda kendiliğinden birikir. Burada yalnız
   * GÖSTERİLİR ve kapatılır; kullanıcı ayrıca "sayım başlat" demek zorunda değildir.
   */
  const tutanakQ = trpc.inventory.countReports.useQuery({ pageSize: 5 }, { staleTime: 15_000 });
  const acikTutanak = (tutanakQ.data?.rows ?? []).find((r) => r.status === "open") ?? null;
  const kapatMut = trpc.inventory.closeCountReport.useMutation();
  async function tutanagiKapat() {
    if (!acikTutanak) return;
    try {
      const r = await kapatMut.mutateAsync({ id: acikTutanak.id });
      await qc.invalidateQueries();
      setMsg({ tone: "ok", text: `${r.code} sayım tutanağı kapatıldı — yazdırabilirsiniz.` });
    } catch (e) { setMsg({ tone: "err", text: (e as Error).message }); }
  }

  const [counted, setCounted] = useState<Record<string, string>>({}); // "kind:id" → sayılan (string)
  const [q, setQ] = useState("");
  const [msg, setMsg] = useState<{ tone: "ok" | "err"; text: string } | null>(null);
  const [confirm, setConfirm] = useState<null | { key: string; itemKind: "stock" | "semi" | "finished"; itemId: string; name: string; onHand: number; countedQty: number; uom: string }>(null);

  const entries = listQ.data?.entries ?? [];
  const rows = listQ.data?.rows ?? [];
  const types = (typesQ.data ?? []) as Array<{ id: string; kind: string; code: string; name: string }>;
  const allItems = (itemsQ.data ?? []) as Array<{ id: string; kind: string; code: string; name: string; itemTypeId: string | null }>;

  // Zaten listede olan girişleri seçeneklerden çıkar (mükerrer eklemeyi baştan engelle).
  const usedTypeIds = new Set(entries.filter((e) => e.kind === "category").map((e) => e.itemTypeId!));
  const usedItemKeys = new Set(entries.filter((e) => e.kind === "item").map((e) => `${e.itemKind}:${e.itemId}`));

  const typeOpts = useMemo<SSGroup[]>(() => {
    const byKind = new Map<string, SSOption[]>();
    for (const t of types) {
      if (usedTypeIds.has(t.id)) continue;
      const arr = byKind.get(t.kind) ?? [];
      arr.push({ value: t.id, label: t.name, tail: t.code });
      byKind.set(t.kind, arr);
    }
    return [...byKind.entries()].map(([k, items]) => ({ group: KIND_LABEL[k] ?? k, items }));
  }, [types, entries]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemOpts = useMemo<SSGroup[]>(() => {
    const byKind = new Map<string, SSOption[]>();
    for (const i of allItems) {
      if (usedItemKeys.has(`${i.kind}:${i.id}`)) continue;
      const arr = byKind.get(i.kind) ?? [];
      arr.push({ value: `${i.kind}:${i.id}`, label: i.name, tail: i.code });
      byKind.set(i.kind, arr);
    }
    return [...byKind.entries()].map(([k, items]) => ({ group: KIND_LABEL[k] ?? k, items }));
  }, [allItems, entries]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const s = q.trim().toLocaleLowerCase("tr");
    if (!s) return rows;
    return rows.filter((r) => r.name.toLocaleLowerCase("tr").includes(s) || r.code.toLocaleLowerCase("tr").includes(s));
  }, [rows, q]);

  async function addCategory(id: string | null) {
    if (!id) return;
    setMsg(null);
    try { await addMut.mutateAsync({ itemTypeId: id }); } catch (e) { setMsg({ tone: "err", text: (e as Error).message }); }
  }
  async function addItem(key: string | null) {
    if (!key) return;
    setMsg(null);
    const [kind, id] = key.split(":");
    try { await addMut.mutateAsync({ itemKind: kind as "stock" | "semi" | "finished", itemId: id! }); } catch (e) { setMsg({ tone: "err", text: (e as Error).message }); }
  }
  async function removeEntry(id: string) {
    setMsg(null);
    try {
      await removeMut.mutateAsync({ id });
      setCounted({}); // liste değişti → girilmiş sayım değerleri bayat kalmasın (giriş geri eklenince eski değer dönmesin)
    } catch (e) { setMsg({ tone: "err", text: (e as Error).message }); }
  }

  async function runApply() {
    if (!confirm) return;
    const c = confirm;
    setConfirm(null); setMsg(null);
    try {
      const res = await applyMut.mutateAsync({ itemKind: c.itemKind, itemId: c.itemId, countedQty: c.countedQty, ...(yeniLotDepo ? { openLotLocationId: yeniLotDepo } : {}) });
      await qc.invalidateQueries();
      setCounted((s) => { const n = { ...s }; delete n[c.key]; return n; }); // satır düzeltildi → giriş temizlenir
      setMsg(res.applied
        ? { tone: "ok", text: `${c.name}: ${fmt(res.onHand)} → ${fmt(c.countedQty)} ${c.uom} (fark ${res.diff > 0 ? "+" : ""}${fmt(res.diff)}) düzeltildi · ${res.lines.map((l) => `${l.lotCode}@${l.locationCode} ${l.qty > 0 ? "+" : ""}${fmt(l.qty)}`).join(" · ")}` }
        : { tone: "ok", text: `${c.name}: fark yok, hareket oluşturulmadı.` });
    } catch (e) { setMsg({ tone: "err", text: (e as Error).message }); }
  }

  /** Satır hesapları: sayılan girildiyse fark + düzeltme uygunluğu. */
  function calc(r: (typeof rows)[number]) {
    const key = `${r.itemKind}:${r.itemId}`;
    const raw = counted[key] ?? "";
    const val = parseQty(raw);
    const diff = val == null ? null : Math.round((val - r.onHand) * 1e4) / 1e4;
    return { key, raw, val, diff, invalid: raw.trim() !== "" && val == null };
  }

  const askApply = (r: (typeof rows)[number], val: number, key: string) =>
    setConfirm({ key, itemKind: r.itemKind, itemId: r.itemId, name: `${r.name} (${r.code})`, onHand: r.onHand, countedQty: val, uom: r.uom });

  const busy = applyMut.isPending || addMut.isPending || removeMut.isPending;

  // ── Liste kurulum şeridi (kategori/kart ekle + chip'ler) — masaüstü ve mobilde ORTAK ──
  const builder = (
    <div className="qc-build">
      <div className="qc-pickers">
        <div className="form-field">
          <label className="lbl">Listede ara</label>
          <div className="dt2-search-box">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input type="search" placeholder="Ürün adı / kodu…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Sayım listesinde ara" />
          </div>
        </div>
        <div className="form-field">
          <label className="lbl">Kategori ekle</label>
          <SearchSelect value={null} options={typeOpts} onChange={addCategory} icon="layers" placeholder="Kategori seç…" searchPlaceholder="Kategori ara…" emptyText={typesQ.isLoading ? "Yükleniyor…" : "Eklenecek kategori yok"} />
        </div>
        <div className="form-field">
          <label className="lbl">Stok kartı ekle</label>
          <SearchSelect value={null} options={itemOpts} onChange={addItem} icon="boxes" placeholder="Kart seç…" searchPlaceholder="Kart adı / kodu ara…" emptyText={itemsQ.isLoading ? "Yükleniyor…" : "Eklenecek kart yok"} />
        </div>
        <div className="form-field">
          <label className="lbl">Son hareket</label>
          <select
            className="form-select"
            value={movedDays ?? ""}
            onChange={(e) => setMovedDays(e.target.value ? Number(e.target.value) : null)}
            aria-label="Son hareket filtresi"
          >
            {MOVED_SEGMENTS.map(([val, lbl]) => <option key={val ?? "all"} value={val ?? ""}>{lbl}</option>)}
          </select>
        </div>
      </div>
      {entries.length > 0 ? (
        <div className="qc-chips">
          {entries.map((e) => (
            <span key={e.id} className={`qc-chip${e.kind === "category" ? " cat" : ""}`}>
              <Icon name={e.kind === "category" ? "layers" : "boxes"} size={12} />
              <span className="t">{e.label ?? "—"}</span>
              <button type="button" className="x" aria-label={`${e.label ?? "Giriş"} listeden çıkar`} disabled={busy} onClick={() => removeEntry(e.id)}>
                <Icon name="x" size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="qc-hint">Sayım listesi boş. Üstten bir <b>kategori</b> ekleyin (o kategorideki tüm kartlar gelir) ya da tek tek <b>stok kartı</b> seçin. Liste size özeldir, kayıtlı kalır.</p>
      )}
    </div>
  );

  const dialog = (
    <ConfirmDialog
      open={!!confirm}
      title="Sayım farkını uygula?"
      message={confirm ? (
        <>
          <b>{confirm.name}</b> için sistem miktarı <b className="mono">{fmt(confirm.onHand)} {confirm.uom}</b>, sayılan <b className="mono">{fmt(confirm.countedQty)} {confirm.uom}</b>.
          <br />
          Fark <b className="mono">{confirm.countedQty - confirm.onHand > 0 ? "+" : ""}{fmt(Math.round((confirm.countedQty - confirm.onHand) * 1e4) / 1e4)} {confirm.uom}</b> stok hareketi olarak yazılacak
          {confirm.countedQty < confirm.onHand ? " (eksik: SKT'si en yakın lottan başlayarak düşülür)" : " (fazla: en çok bakiyeli lota eklenir)"}. Bu işlem geri alınamaz.
          {/* Sistem miktarı 0 + fazlalık → bu kalemin hiç lotu olmayabilir. Depo seçilirse
              sayım kendi lotunu açar (açılış stoğu) ve akış Mal Kabul'e sapmaz. */}
          {confirm.onHand === 0 && confirm.countedQty > 0 ? (
            <div className="form-field" style={{ marginTop: 12, marginBottom: 0 }}>
              <label className="lbl" htmlFor="qc-newloc">Lot yoksa açılacak depo</label>
              <select id="qc-newloc" className="form-select" value={yeniLotDepo} onChange={(e) => setYeniLotDepo(e.target.value)}>
                <option value="">— Seçilmedi (lot yoksa işlem durur) —</option>
                {stokDepolari.map((l) => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
              </select>
              <span className="help">
                Bu kalemin hiç lotu yoksa sayım burada <b>Açılış stoğu</b> olarak lot açar —
                reçete tüketmez. Lot zaten varsa bu seçim kullanılmaz.
              </span>
            </div>
          ) : null}
        </>
      ) : null}
      tone="primary"
      confirmLabel="Düzelt"
      cancelLabel="Vazgeç"
      onConfirm={runApply}
      onCancel={() => setConfirm(null)}
    />
  );

  // ═══ MOBİL ═══
  if (isMobile) {
    return (
      <>
        <div className="d-card"><div className="d-card-body">{builder}</div></div>
        {msg ? <div className={msg.tone === "ok" ? "form-banner-ok" : "form-banner-err"} role={msg.tone === "ok" ? "status" : "alert"}>{msg.text}</div> : null}
        {listQ.isLoading ? <div className="m-empty">Yükleniyor…</div> : shown.length === 0 ? (
          <div className="m-empty">
            {entries.length === 0 ? "Listeye kategori/kart ekleyin."
              : q ? `"${q}" için sonuç yok.`
              : movedDays ? `Son ${movedDays} günde hareket gören kalem yok — filtreyi genişletin.`
              : "Listedeki girişlerin bu tesiste kartı yok."}
          </div>
        ) : shown.map((r) => {
          const { key, raw, val, diff, invalid } = calc(r);
          return (
            <MCard pad key={key}>
              <div className="qc-m-head">
                <span className="n">{r.name}</span>
                <span className="c mono">{r.code}</span>
              </div>
              <div className="qc-m-row">
                <span className="k">Son hareket</span>
                <span className="v qc-moved-cell">{lastMovedLabel(r.lastMovementAt)}</span>
              </div>
              <div className="qc-m-row">
                <span className="k">Sistem</span>
                <span className="v mono">{fmt(r.onHand)} {r.uom}</span>
              </div>
              <div className="qc-m-row">
                <span className="k">Sayılan</span>
                <input className={`form-input qc-input${invalid ? " err" : ""}`} inputMode="decimal" value={raw} placeholder="0"
                  aria-label={`${r.name} sayılan miktar`} disabled={busy}
                  onChange={(e) => setCounted((s) => ({ ...s, [key]: e.target.value }))} />
              </div>
              <div className="qc-m-row">
                <span className="k">Fark</span>
                <span className={`v mono ${diff == null || diff === 0 ? "qc-zero" : diff > 0 ? "qc-plus" : "qc-minus"}`}>
                  {diff == null ? "—" : `${diff > 0 ? "+" : ""}${fmt(diff)} ${r.uom}`}
                </span>
              </div>
              <button type="button" className="btn-md primary" style={{ width: "100%", marginTop: 8 }}
                disabled={busy || val == null || diff === 0} onClick={() => val != null && askApply(r, val, key)}>
                <Icon name="check" size={15} />Düzelt
              </button>
            </MCard>
          );
        })}
        {dialog}
      </>
    );
  }

  // ═══ MASAÜSTÜ ═══
  return (
    <>
      <div className="d-card qc-card">
        <div className="d-card-head">
          <div className="left">
            <span className="t">Hızlı Sayım</span>
            <span className="s">kendi listeni kur · sayılanı gir · farkı tek tıkla düzelt · <b>sayılabilir stok</b> (karantina, QC bekleyen ve SKT'si geçmiş lotlar hariç)</span>
          </div>
          {rows.length > 0 ? <span className="qc-count mono">{rows.length} kalem</span> : null}
        </div>
        <div className="d-card-body">
          {builder}
          {msg ? <div className={msg.tone === "ok" ? "form-banner-ok" : "form-banner-err"} role={msg.tone === "ok" ? "status" : "alert"}>{msg.text}</div> : null}
          {/* Açık tutanak şeridi: sayım bir BELGE olarak birikiyor — kullanıcı bunu görsün,
              bitince kapatıp imzalı çıktı alsın. Kapalı tutanağa satır eklenmez. */}
          {acikTutanak ? (
            <div className="qc-report">
              <span className="ic"><Icon name="fileText" size={15} /></span>
              <span className="tx">
                Açık sayım tutanağı <b className="mono">{acikTutanak.code}</b> — <b>{acikTutanak.lineCount}</b> kalem kayıtlı.
                Farklar bu belgeye işleniyor.
              </span>
              <a className="btn-md secondary sm" href={`/print/sayim-tutanagi?id=${acikTutanak.id}`} target="_blank" rel="noopener noreferrer">
                <Icon name="printer" size={13} />Önizle
              </a>
              <button type="button" className="btn-md primary sm" disabled={kapatMut.isPending} onClick={() => void tutanagiKapat()}>
                <Icon name="check" size={13} />{kapatMut.isPending ? "…" : "Tutanağı Kapat"}
              </button>
            </div>
          ) : null}
        </div>

        {entries.length > 0 ? (
          <>
            <div className="d-card-body flush">
              <table className="dt2 qc-table">
                <thead>
                  <tr>
                    <th>Ürün</th>
                    <th style={{ width: 150 }}>Kategori</th>
                    <th style={{ width: 108 }}>Son Hareket</th>
                    <th style={{ width: 78 }}>Lot</th>
                    <th style={{ width: 120 }}>Sistem</th>
                    <th style={{ width: 140 }}>Sayılan</th>
                    <th style={{ width: 130 }}>Fark</th>
                    <th style={{ width: 108 }}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 ? (
                    <tr><td colSpan={8} className="qc-empty">
                      {q ? `"${q}" için sonuç yok.`
                        : movedDays ? `Son ${movedDays} günde hareket gören kalem yok — "Son hareket" filtresini genişletin.`
                        : "Listedeki girişlerin bu tesiste kartı yok."}
                    </td></tr>
                  ) : shown.map((r) => {
                    const { key, raw, val, diff, invalid } = calc(r);
                    return (
                      <tr key={key}>
                        <td>
                          <span className="qc-name">{r.name}</span>
                          <span className="sec mono">{r.code}</span>
                        </td>
                        <td className="qc-type">{r.itemTypeName ?? "—"}</td>
                        <td className="qc-moved-cell">{lastMovedLabel(r.lastMovementAt)}</td>
                        <td className="cell-num mono">{r.lotCount}</td>
                        <td className="cell-num mono">{fmt(r.onHand)} <span className="unit">{r.uom}</span></td>
                        <td>
                          <input className={`form-input qc-input${invalid ? " err" : ""}`} inputMode="decimal" value={raw} placeholder="—"
                            aria-label={`${r.name} sayılan miktar`} disabled={busy}
                            onChange={(e) => setCounted((s) => ({ ...s, [key]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter" && val != null && diff !== 0) askApply(r, val, key); }} />
                        </td>
                        <td className={`cell-num mono ${diff == null || diff === 0 ? "qc-zero" : diff > 0 ? "qc-plus" : "qc-minus"}`}>
                          {diff == null ? "—" : (
                            <>
                              {diff !== 0 ? <Icon name={diff > 0 ? "chevronUp" : "chevronDown"} size={12} /> : null}
                              {diff > 0 ? "+" : ""}{fmt(diff)}
                            </>
                          )}
                        </td>
                        <td>
                          <button type="button" className="btn-md primary qc-fix" disabled={busy || val == null || diff === 0}
                            onClick={() => val != null && askApply(r, val, key)}>Düzelt</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
      {dialog}
    </>
  );
}
